/**
 * Billing Page - Module marketplace and subscription management
 * Powered by DadyPay crypto payments
 */

import { useState, useEffect } from 'react';
import { useModulesStore } from '../../store/modules';
import { modulesApi, organizationApi } from '../../utils/api';
import {
    Check,
    Crown,
    Rocket,
    Code,
    Briefcase,
    Palette,
    Loader2,
    X,
    Tag,
    Bitcoin,
    Wallet,
    Key,
    Shield,
    CheckCircle,
    AlertCircle,
    Eye,
    EyeOff,
    Copy,
    ExternalLink,
    Search,
    Share2,
    Megaphone,
    Mail,
    Layout,
    BarChart3,
    Users,
    MessageSquare,
    GitBranch,
    FlaskConical,
    Bug,
    Webhook,
    FileText,
    UserPlus,
    DollarSign,
    Scale,
    Presentation,
    Image,
    Video,
    Mic
} from 'lucide-react';

type BillingPeriod = 'monthly' | 'yearly';

// Icon mapping for modules
const iconMap: Record<string, any> = {
    Search,
    Share2,
    Megaphone,
    Mail,
    Layout,
    BarChart3,
    Users,
    MessageSquare,
    Code,
    GitBranch,
    TestTube: FlaskConical,
    Bug,
    Webhook,
    FileText,
    UserPlus,
    DollarSign,
    Scale,
    Presentation,
    Image,
    Video,
    Mic,
    Wallet,
    Rocket,
    Crown,
    Briefcase,
    Palette,
};

// Bundle icon mapping
const bundleIcons: Record<string, any> = {
    Rocket,
    Code,
    Briefcase,
    Palette,
    Crown,
};

export default function Billing() {
    const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
    const [promoCode, setPromoCode] = useState('');
    const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null);

    // DadyPay credentials state
    const [dadyPayStatus, setDadyPayStatus] = useState<{
        enabled: boolean;
        hasApiKey: boolean;
        hasSecretKey: boolean;
        hasWebhookSecret: boolean;
        apiKeyPrefix: string | null;
        webhookUrl: string;
    } | null>(null);
    const [showDadyPayForm, setShowDadyPayForm] = useState(false);
    const [dadyPayCredentials, setDadyPayCredentials] = useState({
        apiKey: '',
        secretKey: '',
        webhookSecret: ''
    });
    const [showSecrets, setShowSecrets] = useState({ apiKey: false, secretKey: false, webhookSecret: false });
    const [dadyPaySaving, setDadyPaySaving] = useState(false);
    const [dadyPayTesting, setDadyPayTesting] = useState(false);
    const [dadyPayMessage, setDadyPayMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const {
        catalog,
        catalogByCategory,
        bundles,
        modules: myModules,
        fetchCatalog,
        fetchMyModules,
        startTrial,
        isLoading
    } = useModulesStore();

    useEffect(() => {
        fetchCatalog();
        fetchMyModules();
        fetchDadyPayStatus();
    }, []);

    const fetchDadyPayStatus = async () => {
        try {
            const response = await organizationApi.getDadyPayStatus();
            setDadyPayStatus(response.data);
        } catch (error) {
            console.error('Failed to fetch DadyPay status:', error);
        }
    };

    const handleSaveDadyPayCredentials = async () => {
        setDadyPaySaving(true);
        setDadyPayMessage(null);
        try {
            await organizationApi.updateDadyPay({
                apiKey: dadyPayCredentials.apiKey || undefined,
                secretKey: dadyPayCredentials.secretKey || undefined,
                webhookSecret: dadyPayCredentials.webhookSecret || undefined,
                enabled: true
            });
            setDadyPayMessage({ type: 'success', text: 'DadyPay credentials saved successfully!' });
            setShowDadyPayForm(false);
            setDadyPayCredentials({ apiKey: '', secretKey: '', webhookSecret: '' });
            fetchDadyPayStatus();
        } catch (error: any) {
            setDadyPayMessage({
                type: 'error',
                text: error.response?.data?.message || 'Failed to save credentials'
            });
        } finally {
            setDadyPaySaving(false);
        }
    };

    const handleTestDadyPayCredentials = async () => {
        setDadyPayTesting(true);
        setDadyPayMessage(null);
        try {
            const response = await organizationApi.testDadyPay();
            setDadyPayMessage({
                type: 'success',
                text: `Credentials valid! ${response.data.supportedCurrencies} cryptocurrencies supported.`
            });
        } catch (error: any) {
            setDadyPayMessage({
                type: 'error',
                text: error.response?.data?.message || 'Invalid credentials'
            });
        } finally {
            setDadyPayTesting(false);
        }
    };

    const handleRemoveDadyPayCredentials = async () => {
        if (!confirm('Remove your DadyPay credentials? Platform default will be used.')) return;
        try {
            await organizationApi.removeDadyPay();
            setDadyPayMessage({ type: 'success', text: 'Credentials removed. Using platform default.' });
            fetchDadyPayStatus();
        } catch (error) {
            setDadyPayMessage({ type: 'error', text: 'Failed to remove credentials' });
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const categories = [
        { id: 'all', name: 'All Modules' },
        { id: 'marketing', name: 'Marketing' },
        { id: 'developer', name: 'Developer' },
        { id: 'business', name: 'Business' },
        { id: 'creative', name: 'Creative' },
    ];

    const filteredModules = selectedCategory === 'all'
        ? catalog
        : catalogByCategory[selectedCategory] || [];

    const hasModule = (moduleType: string) => {
        return myModules.some(m => m.moduleType === moduleType && m.hasAccess);
    };

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return;

        try {
            // API call would go here
            setPromoMessage({ type: 'success', text: 'Promo code applied successfully!' });
            setPromoCode('');
        } catch {
            setPromoMessage({ type: 'error', text: 'Invalid promo code' });
        }
    };

    const handleStartTrial = async (moduleType: string) => {
        const result = await startTrial(moduleType);
        if (result.success) {
            // Show success toast
        }
    };

    const handlePurchaseModule = async (moduleType: string) => {
        setPurchaseLoading(moduleType);
        try {
            const response = await modulesApi.purchaseModule(moduleType, billingPeriod);
            const { paymentUrl } = response.data.data;
            // Open DadyPay checkout in new window
            window.open(paymentUrl, '_blank', 'width=600,height=700');
        } catch (error) {
            console.error('Purchase error:', error);
        } finally {
            setPurchaseLoading(null);
        }
    };

    const handlePurchaseBundle = async (bundleId: string) => {
        setPurchaseLoading(bundleId);
        try {
            const response = await modulesApi.purchaseBundle(bundleId, billingPeriod);
            const { paymentUrl } = response.data.data;
            // Open DadyPay checkout in new window
            window.open(paymentUrl, '_blank', 'width=600,height=700');
        } catch (error) {
            console.error('Purchase error:', error);
        } finally {
            setPurchaseLoading(null);
        }
    };

    if (isLoading && catalog.length === 0) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold">Modules & Billing</h1>
                <p className="text-dark-400 mt-1">Manage your modules and subscription</p>

                {/* DadyPay Badge */}
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 rounded-lg border border-orange-500/30">
                    <Bitcoin className="w-5 h-5 text-orange-400" />
                    <span className="text-sm font-medium">Powered by DadyPay</span>
                    <span className="text-xs text-dark-400">•</span>
                    <span className="text-xs text-dark-300">BTC • ETH • USDT • USDC</span>
                </div>
            </div>

            {/* Billing Period Toggle */}
            <div className="flex items-center justify-center gap-4 p-2 bg-dark-800 rounded-xl w-fit mx-auto">
                <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-6 py-2 rounded-lg transition-all ${billingPeriod === 'monthly'
                        ? 'bg-primary-600 text-white'
                        : 'text-dark-400 hover:text-white'
                        }`}
                >
                    Monthly
                </button>
                <button
                    onClick={() => setBillingPeriod('yearly')}
                    className={`px-6 py-2 rounded-lg transition-all flex items-center gap-2 ${billingPeriod === 'yearly'
                        ? 'bg-primary-600 text-white'
                        : 'text-dark-400 hover:text-white'
                        }`}
                >
                    Yearly
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                        Save 20%
                    </span>
                </button>
            </div>

            {/* Bundles */}
            <div>
                <h2 className="text-xl font-semibold mb-4">📦 Bundles (Best Value)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {bundles.map((bundle) => {
                        const BundleIcon = bundleIcons[bundle.icon] || Rocket;
                        const price = billingPeriod === 'yearly'
                            ? bundle.priceYearly
                            : bundle.priceMonthly;

                        return (
                            <div
                                key={bundle.id}
                                className={`card relative ${bundle.isPopular ? 'border-primary-500' : ''
                                    }`}
                            >
                                {bundle.isPopular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-600 rounded-full text-xs font-medium">
                                        Most Popular
                                    </div>
                                )}

                                <div className="flex items-center gap-3 mb-4">
                                    <div
                                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                                        style={{ backgroundColor: `${bundle.color}20` }}
                                    >
                                        <BundleIcon className="w-6 h-6" style={{ color: bundle.color }} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold">{bundle.name}</h3>
                                        <p className="text-dark-400 text-sm">{bundle.targetPersona}</p>
                                    </div>
                                </div>

                                <p className="text-dark-300 text-sm mb-4">{bundle.description}</p>

                                <div className="mb-4">
                                    <span className="text-3xl font-bold">${price}</span>
                                    <span className="text-dark-400">
                                        /{billingPeriod === 'yearly' ? 'year' : 'mo'}
                                    </span>
                                    {bundle.discountPercent > 0 && (
                                        <span className="ml-2 text-green-400 text-sm">
                                            Save {bundle.discountPercent}%
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-2 mb-6">
                                    {bundle.modules.slice(0, 5).map((m) => (
                                        <div key={m.moduleType} className="flex items-center gap-2 text-sm">
                                            <Check className="w-4 h-4 text-green-400" />
                                            <span>{m.module?.name || m.moduleType}</span>
                                            {!m.isCore && (
                                                <span className="text-xs text-dark-500">Bonus</span>
                                            )}
                                        </div>
                                    ))}
                                    {bundle.modules.length > 5 && (
                                        <p className="text-dark-400 text-sm">
                                            +{bundle.modules.length - 5} more modules
                                        </p>
                                    )}
                                </div>

                                <button
                                    onClick={() => handlePurchaseBundle(bundle.id)}
                                    disabled={purchaseLoading === bundle.id}
                                    className="btn btn-primary w-full flex items-center justify-center gap-2"
                                >
                                    {purchaseLoading === bundle.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Wallet className="w-4 h-4" />
                                            Pay with Crypto
                                        </>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Individual Modules */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">🧩 Individual Modules</h2>

                    {/* Category Filter */}
                    <div className="flex items-center gap-2">
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`px-3 py-1 rounded-lg text-sm transition-all ${selectedCategory === cat.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-dark-800 text-dark-400 hover:text-white'
                                    }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredModules.map((module) => {
                        const ModuleIcon = iconMap[module.icon] || Code;
                        const price = billingPeriod === 'yearly'
                            ? module.priceYearly
                            : module.priceMonthly;
                        const isOwned = hasModule(module.type);

                        return (
                            <div
                                key={module.id}
                                className={`card p-4 ${isOwned ? 'border-green-500/30' : ''}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                                        style={{ backgroundColor: `${module.color}20` }}
                                    >
                                        <ModuleIcon className="w-5 h-5" style={{ color: module.color }} />
                                    </div>

                                    {isOwned ? (
                                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                                            Active
                                        </span>
                                    ) : module.isBeta ? (
                                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                                            Beta
                                        </span>
                                    ) : null}
                                </div>

                                <h3 className="font-medium mb-1">{module.name}</h3>
                                <p className="text-dark-400 text-xs mb-3 line-clamp-2">
                                    {module.description}
                                </p>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="font-semibold">${price}</span>
                                        <span className="text-dark-400 text-sm">
                                            /{billingPeriod === 'yearly' ? 'yr' : 'mo'}
                                        </span>
                                    </div>

                                    {isOwned ? (
                                        <button className="text-sm text-dark-400 hover:text-red-400">
                                            Manage
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleStartTrial(module.type)}
                                                className="text-sm text-primary-400 hover:text-primary-300"
                                            >
                                                Try Free
                                            </button>
                                            <button
                                                onClick={() => handlePurchaseModule(module.type)}
                                                disabled={purchaseLoading === module.type}
                                                className="btn btn-primary btn-sm"
                                            >
                                                {purchaseLoading === module.type ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    'Add'
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Promo Code */}
            <div className="card max-w-md">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-primary-400" />
                    Have a promo code?
                </h3>

                <div className="flex gap-2">
                    <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="input flex-1"
                    />
                    <button
                        onClick={handleApplyPromo}
                        className="btn btn-secondary"
                    >
                        Apply
                    </button>
                </div>

                {promoMessage && (
                    <div className={`mt-3 p-3 rounded-lg text-sm flex items-center justify-between ${promoMessage.type === 'success'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-red-500/10 text-red-400'
                        }`}>
                        {promoMessage.text}
                        <button onClick={() => setPromoMessage(null)}>
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* DadyPay API Credentials */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-500/20 rounded-lg">
                            <Key className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold">DadyPay API Credentials</h3>
                            <p className="text-sm text-dark-400">Configure your own DadyPay keys to receive payments directly</p>
                        </div>
                    </div>
                    {dadyPayStatus?.hasApiKey && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                            <CheckCircle className="w-3 h-3" /> Connected
                        </span>
                    )}
                </div>

                {/* Current Status */}
                {dadyPayStatus && (
                    <div className="mb-4 p-4 bg-dark-800/50 rounded-lg">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="text-dark-400">API Key:</span>
                                <span className={`ml-2 ${dadyPayStatus.hasApiKey ? 'text-green-400' : 'text-red-400'}`}>
                                    {dadyPayStatus.hasApiKey ? dadyPayStatus.apiKeyPrefix : 'Not set'}
                                </span>
                            </div>
                            <div>
                                <span className="text-dark-400">Secret Key:</span>
                                <span className={`ml-2 ${dadyPayStatus.hasSecretKey ? 'text-green-400' : 'text-dark-500'}`}>
                                    {dadyPayStatus.hasSecretKey ? '••••••••' : 'Not set'}
                                </span>
                            </div>
                            <div>
                                <span className="text-dark-400">Webhook Secret:</span>
                                <span className={`ml-2 ${dadyPayStatus.hasWebhookSecret ? 'text-green-400' : 'text-dark-500'}`}>
                                    {dadyPayStatus.hasWebhookSecret ? '••••••••' : 'Not set'}
                                </span>
                            </div>
                            <div>
                                <span className="text-dark-400">Status:</span>
                                <span className={`ml-2 ${dadyPayStatus.enabled ? 'text-green-400' : 'text-yellow-400'}`}>
                                    {dadyPayStatus.enabled ? 'Active' : 'Using Platform Default'}
                                </span>
                            </div>
                        </div>

                        {dadyPayStatus.webhookUrl && (
                            <div className="mt-4 pt-4 border-t border-dark-700">
                                <span className="text-sm text-dark-400">Webhook URL (add to DadyPay dashboard):</span>
                                <div className="flex items-center gap-2 mt-1">
                                    <code className="flex-1 p-2 bg-dark-900 rounded text-xs text-dark-300 overflow-x-auto">
                                        {dadyPayStatus.webhookUrl}
                                    </code>
                                    <button
                                        onClick={() => copyToClipboard(dadyPayStatus.webhookUrl)}
                                        className="p-2 hover:bg-dark-700 rounded"
                                        title="Copy"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => setShowDadyPayForm(!showDadyPayForm)}
                        className="btn btn-secondary"
                    >
                        <Key className="w-4 h-4 mr-2" />
                        {dadyPayStatus?.hasApiKey ? 'Update Credentials' : 'Add Your API Keys'}
                    </button>

                    {dadyPayStatus?.hasApiKey && (
                        <>
                            <button
                                onClick={handleTestDadyPayCredentials}
                                disabled={dadyPayTesting}
                                className="btn btn-secondary"
                            >
                                {dadyPayTesting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Shield className="w-4 h-4 mr-2" />
                                )}
                                Test Connection
                            </button>
                            <button
                                onClick={handleRemoveDadyPayCredentials}
                                className="btn btn-ghost text-red-400 hover:bg-red-500/10"
                            >
                                Remove
                            </button>
                        </>
                    )}

                    <a
                        href="https://pay.dadycoin.com/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost"
                    >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        DadyPay Dashboard
                    </a>
                </div>

                {/* Credentials Form */}
                {showDadyPayForm && (
                    <div className="mt-4 p-4 bg-dark-800/50 rounded-lg space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                API Key <span className="text-dark-400">(pk_...)</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showSecrets.apiKey ? 'text' : 'password'}
                                    value={dadyPayCredentials.apiKey}
                                    onChange={(e) => setDadyPayCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                                    placeholder="pk_live_..."
                                    className="input w-full pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowSecrets(prev => ({ ...prev, apiKey: !prev.apiKey }))}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                                >
                                    {showSecrets.apiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Secret Key <span className="text-dark-400">(sk_...)</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showSecrets.secretKey ? 'text' : 'password'}
                                    value={dadyPayCredentials.secretKey}
                                    onChange={(e) => setDadyPayCredentials(prev => ({ ...prev, secretKey: e.target.value }))}
                                    placeholder="sk_live_..."
                                    className="input w-full pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowSecrets(prev => ({ ...prev, secretKey: !prev.secretKey }))}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                                >
                                    {showSecrets.secretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Webhook Secret <span className="text-dark-400">(whsec_...)</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showSecrets.webhookSecret ? 'text' : 'password'}
                                    value={dadyPayCredentials.webhookSecret}
                                    onChange={(e) => setDadyPayCredentials(prev => ({ ...prev, webhookSecret: e.target.value }))}
                                    placeholder="whsec_..."
                                    className="input w-full pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowSecrets(prev => ({ ...prev, webhookSecret: !prev.webhookSecret }))}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                                >
                                    {showSecrets.webhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={handleSaveDadyPayCredentials}
                                disabled={dadyPaySaving || !dadyPayCredentials.apiKey}
                                className="btn btn-primary"
                            >
                                {dadyPaySaving ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Check className="w-4 h-4 mr-2" />
                                )}
                                Save Credentials
                            </button>
                            <button
                                onClick={() => {
                                    setShowDadyPayForm(false);
                                    setDadyPayCredentials({ apiKey: '', secretKey: '', webhookSecret: '' });
                                }}
                                className="btn btn-ghost"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* DadyPay Message */}
                {dadyPayMessage && (
                    <div className={`mt-4 p-3 rounded-lg text-sm flex items-center justify-between ${dadyPayMessage.type === 'success'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-red-500/10 text-red-400'
                        }`}>
                        <div className="flex items-center gap-2">
                            {dadyPayMessage.type === 'success' ? (
                                <CheckCircle className="w-4 h-4" />
                            ) : (
                                <AlertCircle className="w-4 h-4" />
                            )}
                            {dadyPayMessage.text}
                        </div>
                        <button onClick={() => setDadyPayMessage(null)}>
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Current Subscription */}
            <div className="card">
                <h3 className="font-semibold mb-4">Your Active Modules</h3>

                {myModules.length === 0 ? (
                    <p className="text-dark-400">No active modules. Start a free trial or purchase a bundle above!</p>
                ) : (
                    <div className="space-y-3">
                        {myModules.map((m) => {
                            const moduleInfo = catalog.find(c => c.type === m.moduleType);
                            return (
                                <div key={m.moduleType} className="flex items-center justify-between p-3 bg-dark-800/50 rounded-lg">
                                    <div>
                                        <span className="font-medium">{moduleInfo?.name || m.moduleType}</span>
                                        {m.isTrialing && (
                                            <span className="ml-2 text-xs text-yellow-400">
                                                Trial ends {new Date(m.trialEndsAt!).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-xs ${m.source === 'trial'
                                        ? 'bg-yellow-500/20 text-yellow-400'
                                        : m.source === 'free'
                                            ? 'bg-green-500/20 text-green-400'
                                            : 'bg-primary-500/20 text-primary-400'
                                        }`}>
                                        {m.source === 'trial' ? 'Trial' : m.source === 'free' ? 'Free' : 'Subscribed'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
